import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import analyticsService from '../services/analyticsService';

const SESSION_ID_KEY = 'citi_sess_id';
const KNOWN_VISITOR_KEY = 'citi_known_visitor';

const generateSessionId = () =>
  'sess_' + Math.random().toString(36).substring(2, 11) + '_' + Date.now().toString(36);

const getOrCreateSessionId = () => {
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = generateSessionId();
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
};

const checkFirstVisit = () => {
  if (!localStorage.getItem(KNOWN_VISITOR_KEY)) {
    localStorage.setItem(KNOWN_VISITOR_KEY, '1');
    return true;
  }
  return false;
};

// Exported so other components (e.g. CitiTekConsultation) can obtain the same sessionId
export const getSessionId = () => {
  const existing = sessionStorage.getItem(SESSION_ID_KEY);
  if (existing) return existing;
  const id = generateSessionId();
  sessionStorage.setItem(SESSION_ID_KEY, id);
  return id;
};

const usePageTracking = () => {
  const location = useLocation();
  const { user } = useAuth();
  const prevPath = useRef('');

  useEffect(() => {
    // Never track admins or moderators
    if (user?.role === 'admin' || user?.role === 'moderator') return;

    // Never track admin dashboard pages
    if (location.pathname.startsWith('/admin')) return;

    // Avoid duplicate events on same path (React StrictMode double-effect)
    if (prevPath.current === location.pathname) return;
    prevPath.current = location.pathname;

    const sessionId = getOrCreateSessionId();
    const isFirstVisit = checkFirstVisit();

    analyticsService.trackPageView({
      sessionId,
      path: location.pathname,
      title: document.title || '',
      referrer: document.referrer || '',
      isFirstVisit,
    });
  }, [location.pathname, user]);
};

export default usePageTracking;
